/**
 * LocalStorage implementation of IBookStorage
 * Uses browser's LocalStorage API for persistence
 */

import type { Book } from '../../../contracts/book.ts';
import type { IBookStorage, StorageInfo } from '../../../contracts/storage.ts';

const STORAGE_KEY_PREFIX = 'legere:books:';
const STORAGE_INDEX_KEY = 'legere:books:index';

export class LocalBookStorage implements IBookStorage {
  private storage: Storage;

  constructor(storage: Storage = localStorage) {
    this.storage = storage;
  }

  async addBook(book: Book): Promise<void> {
    const key = this.getBookKey(book.id);
    const serialized = JSON.stringify(book, this.dateReplacer);
    this.storage.setItem(key, serialized);

    // Update index
    await this.updateIndex(book.id, 'add');
  }

  async getBook(id: string): Promise<Book | null> {
    const key = this.getBookKey(id);
    const data = this.storage.getItem(key);

    if (!data) {
      return null;
    }

    return JSON.parse(data, this.dateReviver) as Book;
  }

  async getAllBooks(): Promise<Book[]> {
    const index = await this.getIndex();
    const books: Book[] = [];

    for (const id of index) {
      const book = await this.getBook(id);
      if (book) {
        books.push(book);
      }
    }

    return books;
  }

  async searchBooks(query: string): Promise<Book[]> {
    const allBooks = await this.getAllBooks();
    const lowerQuery = query.toLowerCase();

    return allBooks.filter((book) => {
      return (
        book.title.toLowerCase().includes(lowerQuery) ||
        book.metadata.author?.toLowerCase().includes(lowerQuery) ||
        book.metadata.description?.toLowerCase().includes(lowerQuery) ||
        book.metadata.tags?.some((tag) =>
          tag.toLowerCase().includes(lowerQuery)
        )
      );
    });
  }

  async updateBook(id: string, updates: Partial<Book>): Promise<void> {
    const existing = await this.getBook(id);

    if (!existing) {
      throw new Error(`Book with id ${id} not found`);
    }

    const updated: Book = {
      ...existing,
      ...updates,
      id: existing.id, // Prevent ID changes
      updatedAt: new Date(),
    };

    await this.addBook(updated);
  }

  async deleteBook(id: string): Promise<void> {
    const key = this.getBookKey(id);
    this.storage.removeItem(key);

    // Update index
    await this.updateIndex(id, 'remove');
  }

  async getStorageInfo(): Promise<StorageInfo> {
    let used = 0;

    // Calculate total size of all stored data
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key?.startsWith('legere:')) {
        const value = this.storage.getItem(key);
        if (value) {
          used += key.length + value.length;
        }
      }
    }

    // Convert to bytes (characters are UTF-16, so 2 bytes each)
    used = used * 2;

    // LocalStorage quota is typically 5-10MB
    // We'll use a conservative estimate of 5MB
    const total = 5 * 1024 * 1024;
    const percentage = (used / total) * 100;

    return {
      used,
      total,
      percentage: Math.min(percentage, 100),
    };
  }

  private getBookKey(id: string): string {
    return `${STORAGE_KEY_PREFIX}${id}`;
  }

  private async getIndex(): Promise<string[]> {
    const data = this.storage.getItem(STORAGE_INDEX_KEY);
    if (!data) {
      return [];
    }
    return JSON.parse(data) as string[];
  }

  private async updateIndex(
    id: string,
    operation: 'add' | 'remove'
  ): Promise<void> {
    const index = await this.getIndex();

    if (operation === 'add') {
      if (!index.includes(id)) {
        index.push(id);
      }
    } else {
      const idx = index.indexOf(id);
      if (idx !== -1) {
        index.splice(idx, 1);
      }
    }

    this.storage.setItem(STORAGE_INDEX_KEY, JSON.stringify(index));
  }

  // Helper to serialize Dates
  private dateReplacer(_key: string, value: unknown): unknown {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  }

  // Helper to deserialize Dates
  private dateReviver(key: string, value: unknown): unknown {
    if (typeof value === 'string' && (key === 'createdAt' || key === 'updatedAt' || key === 'lastReadAt')) {
      return new Date(value);
    }
    return value;
  }
}
